import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, TrendingUp } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article4 = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-purple-600 text-white mr-4">
              <TrendingUp className="h-3 w-3 mr-1" />
              {t('category.strategy')}
            </Badge>
            <span className="text-sm text-gray-600">LinkedIn</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            January 8, 2026
            <span className="mx-2">•</span>
            <span>8 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.4.title')}
          </h1>
          
          <img 
            src="https://www.maersk.com/~/media_sc9/maersk/news/news/images/2025/11/14/maersk-smart-truck-at-logistics-hub_1024x576.jpg?w=1056&hash=6ECD22F5638AD4A691AF6FABEFFD5954" 
            alt="Maersk smart truck at logistics hub"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.4.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.4.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.4.section1.content')}
            </p>

            <p className="mb-6">
              {t('article.4.detail1')}
            </p>

            <p className="mb-6">
              {t('article.4.detail2')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.4.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.4.section2.content')}
            </p>

            <p className="mb-6">
              {t('article.4.detail3')}
            </p>

            <p className="mb-6">
              {t('article.4.detail4')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.4.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.4.section3.content')}
            </p>

            <p className="mb-6">
              {t('article.4.detail5')}
            </p>

            <p className="mb-6">
              {t('article.4.detail6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.4.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.4.section4.content')}
            </p>

            <p className="mb-6">
              {t('article.4.detail7')}
            </p>

            <p className="mb-6">
              {t('article.4.detail8')}
            </p>

            <div className="bg-purple-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-2">Key Transformation Areas</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><strong>Adaptive Operations:</strong> Proactive, data-driven strategies</li>
                <li><strong>Real-time Intelligence:</strong> IoT sensors and blockchain integration</li>
                <li><strong>Sustainability Focus:</strong> Green logistics and efficiency convergence</li>
                <li><strong>Technology Integration:</strong> AI, automation, and robotics</li>
                <li><strong>Predictive Analytics:</strong> Machine learning optimization</li>
                <li><strong>Supply Chain Visibility:</strong> End-to-end transparency</li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Kuehne+Nagel official newsletter.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://www.linkedin.com/pulse/looking-ahead-2026-from-volatility-velocity-plus-kz8je/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article4;